import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, BarChart3, Check, Loader2, PenLine, Sparkles, Target, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { VIDEO_STYLES, type VideoStyle, type VideoGenerationRequest } from "../types/videoStudio.types";
import { VideoStudioGallery } from "./VideoStudioGallery";
import {
  VideoStudioSidebar,
  type ProductContextStatus,
  type VideoAngleOption,
} from "./VideoStudioSidebar";
import { PreviewApproval } from "./PreviewApproval";
import { AvatarWizardModal, type AvatarCommit } from "./AvatarWizardModal";
import useVideoGenerationStore from "@/store/videoGenerationStore";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import { useVideoDraftFlag } from "../hooks/useVideoDraftFlag";
import { getVideoProductInputContext } from "@/services/ads/videos/videoDraftService";
import { AUDIO_MODE, type AvatarRecommendationItem, type VideoProductContextResponse, type VideoProductInput } from "@/services/ads/videos/videoDraft.types";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";

interface Step2VideoStudioProps {
  product: VideoProductInput;
  onClose: () => void;
  onBackToProduct?: () => void;
  onStageChange?: (stage: StudioVisualStage) => void;
}

type StudioStage = "style" | "angle" | "avatar" | "configure";
type StudioVisualStage = StudioStage | "avatar-loading";

type SelectedAngle = {
  name: string;
  description: string;
};

const MODELING_STYLE_IDS = new Set(["product-modeling-voiceover", "product-modeling"]);
const PRIMARY_VIDEO_STYLE_ID = "product-modeling-voiceover";
const DEFAULT_VIDEO_STYLE = VIDEO_STYLES.find(
  (style) => style.enabled && style.id === PRIMARY_VIDEO_STYLE_ID
) ?? null;
const ANGLE_ANALYSIS_LOADING_DELAY_MS = 6000;

type ProductContextCacheValue = {
  context: VideoProductContextResponse | null;
  status: ProductContextStatus;
};

const productContextSessionCache = new Map<string, ProductContextCacheValue>();
const productContextSessionInFlight = new Map<string, Promise<ProductContextCacheValue>>();

function productContextCacheKey(productId?: string | null, websiteId?: string | null) {
  return `${productId ?? ""}::${websiteId ?? ""}`;
}

function productInputCacheKey(product: VideoProductInput) {
  return product.product_id
    ? productContextCacheKey(product.product_id, product.website_id)
    : JSON.stringify({
        source: product.source,
        name: product.name,
        description: product.description,
        image_url: product.image_url ?? product.images?.[0] ?? null,
      });
}

function createVideoDraftReferenceId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `video-draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function getVideoProductContextWithSessionCache(
  product: VideoProductInput,
  forceRefresh = false
): Promise<ProductContextCacheValue> {
  const cacheKey = productInputCacheKey(product);
  if (!forceRefresh) {
    const cached = productContextSessionCache.get(cacheKey);
    if (cached) return cached;
    const inFlight = productContextSessionInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  }

  const request = getVideoProductInputContext(product).then((response) => {
    const context = response.res ?? null;
    const hasAngles = (context?.angles?.length ?? 0) > 0;
    const value: ProductContextCacheValue = {
      context,
      status: hasAngles ? "ready" : "fallback",
    };
    productContextSessionCache.set(cacheKey, value);
    return value;
  });

  if (!forceRefresh) {
    productContextSessionInFlight.set(cacheKey, request);
    request.finally(() => {
      if (productContextSessionInFlight.get(cacheKey) === request) {
        productContextSessionInFlight.delete(cacheKey);
      }
    });
  }

  return request;
}

function shortProductName(value?: string | null) {
  const normalized = (value ?? "").trim().replace(/\s+/g, " ");
  if (!normalized) return "";
  const primary = normalized.split(/\s[-–—|]\s|[,;]/)[0]?.trim() || normalized;
  if (primary.length <= 34) return primary;
  const cut = primary.slice(0, 34).trimEnd();
  const lastSpace = cut.lastIndexOf(" ");
  return lastSpace >= 16 ? cut.slice(0, lastSpace) : cut;
}

function inferPreferredVoiceGender(avatar: AvatarCommit | null): "female" | "male" | null {
  const explicitGender = avatar?.config?.gender;
  if (explicitGender === "female" || explicitGender === "male") return explicitGender;

  const text = [
    avatar?.config?.custom_prompt,
    avatar?.displayName,
    avatar?.config?.recommendation_label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/\b(hombre|male|man|masculino|chico|señor|senor|papá|papa|padre)\b/.test(text)) return "male";
  if (/\b(mujer|female|woman|femenina|femenino|chica|señora|senora|mamá|mama|madre)\b/.test(text)) return "female";
  return null;
}

export function Step2VideoStudio({ product, onClose, onBackToProduct, onStageChange }: Step2VideoStudioProps) {
  const [videoDraftReferenceId] = useState(createVideoDraftReferenceId);
  const [selectedStyle, setSelectedStyle] = useState<VideoStyle | null>(DEFAULT_VIDEO_STYLE);
  const [stage, setStage] = useState<StudioStage>("style");
  const [isGenerating, setIsGenerating] = useState(false);
  // Phase 2 V2 — Avatar Wizard state. When `avatarWizardOpen` is true we
  // render the wizard in place of the studio panel (takeover UX, not a
  // popover) — the wizard is the user's only focus at that moment.
  const [avatarWizardOpen, setAvatarWizardOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarCommit | null>(null);
  const [selectedAngle, setSelectedAngle] = useState<SelectedAngle | null>(null);
  const preferredVoiceGender = inferPreferredVoiceGender(selectedAvatar);
  const [avatarRecommendationCache, setAvatarRecommendationCache] = useState<AvatarRecommendationItem[]>([]);
  const [productContext, setProductContext] = useState<VideoProductContextResponse | null>(null);
  const [productContextStatus, setProductContextStatus] = useState<ProductContextStatus>("idle");
  const startVideoGeneration = useVideoGenerationStore((s) => s.startGeneration);
  const startDraft = useVideoDraftStore((s) => s.startDraft);
  const resetDraft = useVideoDraftStore((s) => s.reset);
  const storeStatus = useVideoDraftStore((s) => s.storeStatus);
  // Modal-owned draft states. Post-approve rendering lives in the gallery
  // in-flight store, so it must not hijack a new Create Video modal.
  const draftMode = [
    "directing",
    "ready",
    "approving",
    "rejecting",
    "editing_script",
    "regenerating_image",
    "regenerating_style",
    "generating_audio",
    "error",
    "done",
  ].includes(storeStatus);

  // Phase 2: feature flag — only enable draft preview for styles that have a
  // director_v1 agent created in agent-config (currently animated-problem).
  // For everything else we fall through to the legacy direct-render flow.
  const draftEnabled = useVideoDraftFlag(selectedStyle?.id);
  const avatarRecommendationContextKey = useMemo(() => {
    if (!selectedAngle) return null;
    return JSON.stringify({
      product_id: product.product_id ?? "",
      website_id: product.website_id ?? "",
      angle_name: selectedAngle.name,
      angle_description: selectedAngle.description,
    });
  }, [product.product_id, product.website_id, selectedAngle]);

  const isAvatarRecommendationLoadingStage =
    stage === "avatar" &&
    !!selectedAngle &&
    avatarRecommendationCache.length < 4;

  useEffect(() => {
    onStageChange?.(isAvatarRecommendationLoadingStage ? "avatar-loading" : stage);
  }, [isAvatarRecommendationLoadingStage, onStageChange, stage]);

  useEffect(() => {
    setAvatarRecommendationCache([]);
  }, [avatarRecommendationContextKey]);

  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      const cacheKey = productInputCacheKey(product);
      const cached = productContextSessionCache.get(cacheKey);
      if (cached) {
        setProductContext(cached.context);
        setProductContextStatus(cached.status);
      } else {
        setProductContextStatus("loading");
      }

      try {
        const response = await getVideoProductContextWithSessionCache(product, !!cached);
        if (cancelled) return;

        setProductContext(response.context);
        setProductContextStatus(response.status);
      } catch {
        if (!cancelled) {
          if (!cached) {
            setProductContext(null);
            setProductContextStatus("fallback");
          }
        }
      }
    };

    loadContext();

    return () => {
      cancelled = true;
    };
  }, [product]);

  const handleSelectStyle = (style: VideoStyle) => {
    setSelectedStyle(style);
    setSelectedAvatar(null);
    setSelectedAngle(null);
  };

  const handleContinueStyle = () => {
    if (!selectedStyle) return;

    if (MODELING_STYLE_IDS.has(selectedStyle.id)) {
      setStage("angle");
      return;
    }

    setStage("configure");
  };

  const handleClose = () => {
    if (draftMode) resetDraft();
    onClose();
  };

  const handleSubmittedToGallery = () => {
    // Once the preview has been approved, the gallery in-flight store owns
    // the long render. Release this modal-scoped draft so the user can start
    // another video while the previous one is still being created.
    resetDraft();
    onClose();
  };

  const handleGenerate = async (partialPayload: VideoGenerationRequest) => {
    setIsGenerating(true);

    // ── New draft preview flow (Phase 2) ──
    if (draftEnabled && selectedStyle) {
      await startDraft({
        product_id: product.product_id,
        product_input: product,
        style_id: selectedStyle.id,
        duration: parseInt(partialPayload.duration || "30"),
        language: partialPayload.language || "es",
        sale_angle_name: partialPayload.angle,
        sale_angle_description: partialPayload.angle_description,
        user_instruction: partialPayload.user_prompt,
        // Phase 6 — UGC + Modeling: forward avatar_config to backend.
        avatar_config: partialPayload.avatar_config ?? null,
        // Phase 2 V2 — voice_config drives TTS generation for modeling
        // voice-over. The sidebar builds this object (type + voice +
        // speed/stability); the backend reads type to decide whether to
        // run ElevenLabs or leave the draft silent.
        voice_config: partialPayload.voice_config ?? null,
        // Phase 2 V3 — the new canonical audio-mode switch (professional,
        // own, fast). Backend prefers this over voice_config.type when
        // both are present. See VideoDraftService.parseAudioMode.
        audio_mode: partialPayload.audio_mode ?? null,
        // Phase 2 V2 — pass the already-rendered portrait so the backend
        // skips the Gemini Image regeneration step. Huge latency + cost
        // win + keeps the exact portrait the user saw in the wizard.
        pre_generated_base_image_url: selectedAvatar?.portraitUrl ?? null,
      });
      setIsGenerating(false);
      // draftMode is now derived from storeStatus — no need to set it manually.
      // When startDraft succeeds, storeStatus transitions to "directing" → draftMode = true.
      return;
    }

    // ── Legacy flow (untouched) ──
    const success = await startVideoGeneration(
      {
        product_id: product.product_id ?? undefined,
        video_content_type: partialPayload.style_id || "UGC",
        style_id: partialPayload.style_id,
        language: partialPayload.language || "es",
        duration: parseInt(partialPayload.duration || "10"),
        tone: partialPayload.tone || undefined,
        instruction: partialPayload.user_prompt,
        angle: partialPayload.angle,
        angle_description: partialPayload.angle_description,
        problem_hint: partialPayload.problem_hint,
        avatar_config: partialPayload.avatar_config ? {
          gender: partialPayload.avatar_config.gender,
          age_range: partialPayload.avatar_config.age_range,
          skin_tone: partialPayload.avatar_config.skin_tone,
          hair: partialPayload.avatar_config.hair,
          vibe: partialPayload.avatar_config.vibe,
          setting: partialPayload.avatar_config.setting,
        } : undefined,
      },
      product.name
    );

    setIsGenerating(false);
    if (success) onClose();
  };

  const handleStartModelingDraft = async (avatar: AvatarCommit) => {
    if (!selectedStyle || !selectedAngle) return;

    setSelectedAvatar(avatar);
    setIsGenerating(true);

    await startDraft({
      reference_id: videoDraftReferenceId,
      product_id: product.product_id,
      product_input: product,
      style_id: selectedStyle.id,
      duration: 30,
      language: "es",
      sale_angle_name: selectedAngle.name,
      sale_angle_description: selectedAngle.description,
      user_instruction: "",
      avatar_config: avatar.config ?? null,
      voice_config: {
        type: "tts",
        provider: "gemini",
        accent_city: "Medellín",
        speed: 1,
        stability: 0.5,
        language_code: "es",
      },
      audio_mode: AUDIO_MODE.PROFESSIONAL,
      pre_generated_base_image_url: avatar.portraitUrl ?? null,
    });

    setIsGenerating(false);
  };

  // Phase 2: when draftMode is true the user already triggered the new draft
  // flow and should see the preview/approval screen instead of the studio.
  if (draftMode) {
    return (
      <PreviewApproval
        onBack={() => {
          resetDraft();
          // storeStatus goes to "idle" → draftMode becomes false automatically.
        }}
        onDone={handleClose}
        onSubmitted={handleSubmittedToGallery}
        preferredVoiceGender={preferredVoiceGender}
      />
    );
  }

  if (stage === "avatar" && selectedStyle && selectedAngle) {
    return (
      <div className="flex flex-col h-full max-h-full overflow-hidden relative bg-white">
        <AvatarWizardModal
          productId={product.product_id}
          productInput={product}
          websiteId={product.website_id ?? productContext?.website_id ?? null}
          productName={product.name}
          productDescription={product.description}
          productImageUrl={product.image_url ?? null}
          selectedAngle={selectedAngle}
          draftReferenceId={videoDraftReferenceId}
          initialGeneratedRecommendations={avatarRecommendationCache}
          onGeneratedRecommendationsChange={setAvatarRecommendationCache}
          onBack={() => setStage("angle")}
          onCommit={handleStartModelingDraft}
        />
      </div>
    );
  }

  if (stage === "style") {
    return (
      <StudioShell
        product={product}
        onBack={onBackToProduct}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 bg-white">
            <VideoStudioGallery
              selectedStyleId={selectedStyle?.id ?? null}
              onSelectStyle={handleSelectStyle}
              onContinue={handleContinueStyle}
              canContinue={!!selectedStyle}
            />
          </div>
        </div>
      </StudioShell>
    );
  }

  if (stage === "angle" && selectedStyle) {
    return (
      <StudioShell
        product={product}
      >
        <VideoAngleStep
          product={product}
          productContext={productContext}
          productContextStatus={productContextStatus}
          initialSelection={selectedAngle}
          onBack={() => setStage("style")}
          onContinue={(angle) => {
            setSelectedAngle(angle);
            setStage("avatar");
          }}
        />
      </StudioShell>
    );
  }

  // Phase 2 V2 — Avatar Wizard takes over the entire studio panel visually
  // when active. BUT we can't swap the JSX tree (early-return the wizard
  // instead of the studio) because that unmounts VideoStudioSidebar and
  // loses all its local state — audioMode, voice selections, uploaded
  // audio URL, etc. The user could open the wizard to pick an avatar,
  // come back, and silently lose the sidebar state.
  //
  // Fix (Phase 2 V3): always render the studio tree, and overlay the wizard
  // on top with `display:none` toggling the studio visibility. That keeps
  // the sidebar mounted, so its state survives wizard open/close cycles.
  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden relative">
      {avatarWizardOpen && (
        <div className="absolute inset-0 z-50 bg-white">
          <AvatarWizardModal
            productId={product.product_id}
            productInput={product}
            websiteId={product.website_id ?? productContext?.website_id ?? null}
            productName={product.name}
            productDescription={product.description}
            productImageUrl={product.image_url ?? null}
            selectedAngle={selectedAngle}
            draftReferenceId={videoDraftReferenceId}
            initialGeneratedRecommendations={avatarRecommendationCache}
            onGeneratedRecommendationsChange={setAvatarRecommendationCache}
            onBack={() => setAvatarWizardOpen(false)}
            onCommit={(avatar) => {
              setSelectedAvatar(avatar);
              setAvatarWizardOpen(false);
            }}
          />
        </div>
      )}
      <div
        className="flex flex-col h-full max-h-full overflow-hidden"
        style={avatarWizardOpen ? { display: "none" } : undefined}
      >
      {/* Header — subtle indigo-tinted gradient so the "tapa" reads as
          branded without drowning the content below. */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200/70 bg-slate-50 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-md bg-white ring-1 ring-slate-200">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="max-w-[280px] truncate text-base font-semibold leading-tight text-foreground">
              {shortProductName(product.name)}
            </h2>
          </div>
        </div>
      </div>

      {/* Main content — fixed 60/40 split. The format gallery stays stable:
          selecting a format only marks its card and updates the right-side
          configuration panel. It should not expand the selected card into a
          large preview, because that makes the layout jump and hides the rest
          of the available formats. */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[60%] min-w-0 min-h-0 overflow-hidden flex-none">
          <VideoStudioGallery
            selectedStyleId={selectedStyle?.id ?? null}
            onSelectStyle={handleSelectStyle}
          />
        </div>

        <VideoStudioSidebar
          product={product}
          selectedStyle={selectedStyle}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
          avatar={selectedAvatar}
          onOpenAvatarWizard={() => setAvatarWizardOpen(true)}
        />
      </div>
      </div>
    </div>
  );
}

function StudioShell({
  product,
  onBack,
  children,
}: {
  product: VideoProductInput;
  onBack?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full max-h-[90vh] flex-col overflow-hidden bg-white">
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-slate-50 px-6 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mr-0.5 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/80 hover:text-foreground"
              aria-label="Volver a seleccionar producto"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : null}
          <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-md bg-white ring-1 ring-slate-200">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <p className="max-w-[260px] truncate text-base font-semibold leading-tight text-foreground">
              {shortProductName(product.name)}
            </p>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

function normalizeAngleTitle(name: string, description?: string | null) {
  const trimmedName = name.trim();
  const trimmedDescription = description?.trim();
  if (!trimmedName && trimmedDescription) {
    return firstReadableAngleClause(trimmedDescription);
  }
  if ((trimmedName.includes("...") || trimmedName.includes("…") || endsWithDanglingAngleWord(trimmedName)) && trimmedDescription) {
    return firstReadableAngleClause(trimmedDescription);
  }
  return trimmedName;
}

function firstReadableAngleClause(value: string) {
  const normalized = value.trim().replace(/\s+/g, " ");
  const clauses = normalized.split(/[;:—–.]/).map((part) => part.trim()).filter(Boolean);
  return clauses.find((clause) => clause.length >= 18 && !endsWithDanglingAngleWord(clause)) ?? normalized.replace(/\.$/, "");
}

function endsWithDanglingAngleWord(value: string) {
  return /\b(a|al|ante|bajo|con|contra|de|del|desde|en|entre|hacia|hasta|la|las|lo|los|para|por|según|sin|sobre|tras|un|una|unos|unas|y|o)$/i.test(
    value.trim(),
  );
}

function VideoAngleStep({
  product,
  productContext,
  productContextStatus,
  initialSelection,
  onBack,
  onContinue,
}: {
  product: VideoProductInput;
  productContext: VideoProductContextResponse | null;
  productContextStatus: ProductContextStatus;
  initialSelection: SelectedAngle | null;
  onBack: () => void;
  onContinue: (angle: SelectedAngle) => void;
}) {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAngle, setCustomAngle] = useState("");

  const angleOptions = useMemo<VideoAngleOption[]>(() => {
    if (productContextStatus === "ready" && productContext?.angles?.length) {
      const contextOptions = productContext.angles.map((item, index) => {
        const description = item.description || item.name;
        return {
          id: item.id || `context-angle-${index}`,
          name: normalizeAngleTitle(item.name, description),
          description,
          proof: item.proof ?? undefined,
          badge: item.badge ?? (index === 0 ? "Base del website" : "Variación"),
          source: item.source ?? undefined,
        };
      });

      if (contextOptions.length) return contextOptions.slice(0, 5);
    }

    return [];
  }, [productContext, productContextStatus]);

  useEffect(() => {
    if (productContextStatus === "loading") return;
    if (selectedOptionId || customOpen) return;

    if (initialSelection) {
      const match = angleOptions.find((option) => option.name === initialSelection.name);
      if (match) {
        setSelectedOptionId(match.id);
        return;
      }
      setCustomAngle(initialSelection.name);
      setCustomOpen(true);
      return;
    }

    if (angleOptions.length) {
      setSelectedOptionId(angleOptions[0].id);
    }
  }, [angleOptions, customOpen, initialSelection, productContextStatus, selectedOptionId]);

  const resolvedSelectedOptionId =
    selectedOptionId ?? (!customOpen ? angleOptions[0]?.id ?? null : null);

  const selectedOption = useMemo(
    () => angleOptions.find((option) => option.id === resolvedSelectedOptionId) ?? null,
    [angleOptions, resolvedSelectedOptionId]
  );

  const selectedAngle =
    customOpen && customAngle.trim()
      ? {
          name: customAngle.trim(),
          description: customAngle.trim(),
        }
      : selectedOption
        ? {
            name: selectedOption.name,
            description: selectedOption.description || selectedOption.name,
          }
        : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-slate-50/70">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-4">
        <div className="mx-auto max-w-[680px]">
          <div className="mb-2 min-w-0">
            <h3 className="text-base font-semibold text-slate-950">
              Elige el ángulo del anuncio
            </h3>
          </div>

          {productContextStatus === "loading" ? <VideoAngleLoading productName={product.name} /> : null}

          {productContextStatus !== "loading" ? (
            <div className="space-y-3 transition-all">
              {angleOptions.length ? (
                <div className="space-y-2">
                  {angleOptions.map((option) => {
                    const selected = resolvedSelectedOptionId === option.id && !customOpen;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setCustomOpen(false);
                          setSelectedOptionId(option.id);
                        }}
                        className={cn(
                          "w-full rounded-xl px-4 py-3 text-left transition-all",
                          selected
                            ? "border-[1.5px] border-fluxi/40 bg-white text-gray-900 shadow-[0_10px_30px_rgba(67,104,229,0.10)]"
                            : "border border-[#E2E8F0] bg-white text-gray-600 hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              <p className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900">
                                {option.name}
                              </p>
                              {selected ? <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-fluxi" /> : null}
                            </div>
                            <p className="mt-1 text-sm leading-relaxed text-gray-500">
                              {option.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-indigo-100 bg-white px-4 py-3 text-sm text-gray-500">
                  No encontramos ángulos guardados ni generados para este producto. Puedes crear uno personalizado.
                </p>
              )}

              <div
                className={cn(
                  "rounded-xl border transition-all",
                  customOpen
                    ? "border-fluxi/40 bg-white shadow-[0_10px_30px_rgba(67,104,229,0.10)]"
                    : "border-dashed border-[#E2E8F0] bg-white hover:border-indigo-200"
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    setCustomOpen(true);
                    setSelectedOptionId(null);
                  }}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left"
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full",
                      customOpen ? "bg-fluxi text-white" : "bg-indigo-50 text-fluxi"
                    )}
                  >
                    <PenLine className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-2">
                      <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900">
                        Crear ángulo propio
                      </span>
                      <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-fluxi">
                        Personalizado
                      </span>
                      {customOpen ? <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-fluxi" /> : null}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-gray-500">
                      Escribe una dirección puntual si quieres salirte de las recomendaciones.
                    </span>
                  </span>
                </button>

                {customOpen ? (
                  <div className="mx-4 mb-4 border-t border-indigo-100 pt-3">
                    <textarea
                      value={customAngle}
                      onChange={(event) => setCustomAngle(event.target.value.slice(0, 240))}
                      placeholder="Ej: mostrar una rutina real donde la persona vuelve a progresar sin sonar publicitario."
                      rows={3}
                      className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-gray-800 outline-none transition focus:border-fluxi/50 focus:ring-1 focus:ring-fluxi/20"
                      autoFocus
                    />
                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-500">
                      <span>Se usará como base para el guion del video.</span>
                      <span className="tabular-nums">{customAngle.length}/240</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-shrink-0 border-t border-indigo-100/70 bg-white px-6 py-1.5">
        <div className="mx-auto flex w-full max-w-[680px] items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 text-sm text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <button
            type="button"
            disabled={!selectedAngle}
            onClick={() => selectedAngle && onContinue(selectedAngle)}
            className={cn(
              "ml-auto flex items-center gap-2 rounded-full px-7 py-2.5 text-sm font-medium text-white transition-all",
              selectedAngle
                ? "bg-fluxi hover:bg-fluxi-dark"
                : "cursor-not-allowed bg-[#E2E8F0]"
            )}
          >
            Continuar
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

type LoadingScreenStep = {
  icon: LucideIcon;
  label: string;
};

function VideoAngleLoading({ productName }: { productName: string }) {
  const [showAnalysisAnimation, setShowAnalysisAnimation] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setShowAnalysisAnimation(true),
      ANGLE_ANALYSIS_LOADING_DELAY_MS
    );
    return () => window.clearTimeout(timeout);
  }, []);

  if (!showAnalysisAnimation) {
    return <VideoAngleSkeleton productName={productName} />;
  }

  return <VideoAngleAnalysisLoading />;
}

function VideoAngleSkeleton({ productName }: { productName: string }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border-[1.5px] border-fluxi/20 bg-white px-4 py-3 shadow-[0_10px_30px_rgba(67,104,229,0.08)]">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-5 w-5 rounded-full bg-indigo-50" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-3/5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/2 animate-shine bg-white/70" />
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100" />
            <div className="h-3 w-4/5 rounded-full bg-slate-100" />
          </div>
        </div>
      </div>
      {[0, 1, 2].map((item) => (
        <div key={item} className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-5 w-5 rounded-full bg-slate-100" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-1/2 rounded-full bg-slate-100" />
              <div className="h-3 w-full rounded-full bg-slate-100" />
              <div className="h-3 w-2/3 rounded-full bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
      <p className="text-center text-[11px] text-slate-400">
        Cargando ángulos de {shortProductName(productName) || "este producto"}...
      </p>
    </div>
  );
}

function VideoAngleAnalysisLoading() {
  const { language } = useAuth();
  const lang = language as Language;
  const [activeStep, setActiveStep] = useState(0);
  const steps: LoadingScreenStep[] = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[lang] ?? "Haciendo investigación de mercado...",
    },
    {
      icon: Target,
      label: dictionary["loading-personalized-sales-angles"]?.[lang] ?? "Buscando reseñas de productos similares...",
    },
    {
      icon: BarChart3,
      label: dictionary["optimizing-sections"]?.[lang] ?? "Encontrando dolores de usuarios reales...",
    },
  ];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % steps.length);
    }, 2500);

    return () => window.clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center px-4 py-6">
      <div className="relative mb-7">
        <div className="flex h-20 w-20 animate-pulse items-center justify-center rounded-full bg-gradient-to-br from-fluxi to-fluxi">
          <Sparkles className="h-10 w-10 text-white" />
        </div>
        <div className="absolute inset-0 h-20 w-20 animate-ping rounded-full bg-gradient-to-br from-fluxi to-fluxi opacity-20" />
      </div>

      <h2 className="mb-2 text-center text-xl font-bold text-gray-900 md:text-2xl">
        {dictionary["fluxi-analyzing-title"]?.[lang] ?? "Fluxi está analizando tu producto"}
      </h2>
      <p className="mb-8 max-w-md text-center text-sm text-[#64748B]">
        {dictionary["fluxi-analyzing-subtitle"]?.[lang] ?? "Estamos encontrando los mejores ángulos de venta para tu producto"}
      </p>

      <div className="w-full max-w-sm space-y-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={step.label}
              className={cn(
                "flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all duration-500",
                isActive
                  ? "border-fluxi/20 bg-[#F5F8FF] shadow-sm"
                  : isDone
                    ? "border-green-200 bg-[#F0FDF4]"
                    : "border-transparent bg-[#F8FAFC]"
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-500",
                  isActive
                    ? "bg-fluxi text-white"
                    : isDone
                      ? "bg-green-500 text-white"
                      : "bg-[#E2E8F0] text-gray-400"
                )}
              >
                {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span
                className={cn(
                  "text-sm font-medium transition-all duration-500",
                  isActive ? "text-fluxi" : isDone ? "text-green-600" : "text-gray-400"
                )}
              >
                {step.label}
              </span>
              {isActive ? (
                <div className="ml-auto">
                  <Loader2 className="h-4 w-4 animate-spin text-fluxi" />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
